/**
 * Encryption utility functions for handling buffer conversions
 * and working with dexie-encrypted
 */

/**
 * Convert an ArrayBuffer or Uint8Array to a hexadecimal string
 */
export function bufferToHex(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert a hexadecimal string to a Uint8Array
 */
export function hexToBuffer(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

/**
 * Convert an ArrayBuffer or Uint8Array to a base64 string
 */
export function bufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert a base64 string to a Uint8Array
 */
export function base64ToBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Derive an encryption key from WebAuthn credential using PBKDF2
 * Returns a Uint8Array of 32 bytes for use with dexie-encrypted
 *
 * Includes domain separation to prevent key reuse across different contexts
 * and length-prefixed userId to prevent concatenation ambiguities
 *
 * @param rawId - Raw credential ID from WebAuthn
 * @param salt - Cryptographic salt for key derivation
 * @param userId - User ID to mix into key material
 * @returns Uint8Array of 32 bytes (256 bits) for AES-GCM encryption
 */
export async function deriveEncryptionKey(
  rawId: Uint8Array,
  salt: Uint8Array,
  userId: number
): Promise<Uint8Array> {
  // Domain separation string to prevent key reuse across different contexts
  const domain = 'monami.offline.db.encryption.v1';
  const domainBytes = new TextEncoder().encode(domain);

  // Length-prefix userId to prevent ambiguities (e.g., "12"+"3" vs "1"+"23")
  const userIdStr = userId.toString();
  const userIdLength = new Uint8Array([userIdStr.length]);
  const userIdBytes = new TextEncoder().encode(userIdStr);

  // Combine: domain | rawId | length(userId) | userId
  const keyMaterial = new Uint8Array([
    ...domainBytes,
    ...rawId,
    ...userIdLength,
    ...userIdBytes
  ]);

  // Import key material for PBKDF2
  const importedKey = await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    'PBKDF2',
    false,
    ['deriveBits']
  );

  // Derive 256 bits (32 bytes) using PBKDF2 with 100k iterations
  const keyBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 100000,
      hash: 'SHA-256',
    },
    importedKey,
    256 // 256 bits = 32 bytes
  );

  return new Uint8Array(keyBits);
}

